import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { resolvePoKName } from '../../utils/domainUtils.js';
import { PURPLE, INK, LINE } from '../sections/theme.js';

// Localized URLs for the best-platforms comparison post, used by the
// <comparisonPost> placeholder in FAQ answers (home.faqs.41).
const BEST_PLATFORMS_POST_URL = {
    en: '/blog/posts/best-digital-credential-platforms-2026',
    es: '/blog/es/posts/mejores-plataformas-credenciales-digitales-2026',
    pt: '/blog/pt/posts/melhores-plataformas-credenciais-digitais-2026',
};

function ChevronIcon() {
    return (
        <Box
            component="svg"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            sx={{
                width: 22,
                height: 22,
                color: PURPLE,
                flexShrink: 0,
            }}
        >
            <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </Box>
    );
}

export function FaqItem({ idx, questionComponent = 'h3', namespace = 'home.faqs' }) {
    const { i18n } = useTranslation();
    const comparisonPostUrl = BEST_PLATFORMS_POST_URL[i18n.language] ?? BEST_PLATFORMS_POST_URL.en;
    return (
        <Accordion
            disableGutters
            elevation={0}
            // MUI already wraps the summary in a heading; that wrapper is the page's heading for
            // this question, so the question text below renders as plain inline content instead of
            // a second, nested one.
            slotProps={{ heading: { as: questionComponent } }}
            sx={{
                background: '#fff',
                border: `1px solid ${LINE}`,
                borderRadius: '20px',
                overflow: 'hidden',
                '&:before': { display: 'none' },
                '&:first-of-type, &:last-of-type': { borderRadius: '20px' },
            }}
        >
            <AccordionSummary
                expandIcon={<ChevronIcon />}
                aria-controls={`faq_panel_${idx}`}
                id={`faq_header_${idx}`}
                sx={{
                    px: { xs: 2.5, md: 3.5 },
                    minHeight: 64,
                    transition: 'background 0.15s ease',
                    '&:hover': { background: '#f7f3ff' },
                    '& .MuiAccordionSummary-content': { my: 2 },
                    '& .MuiAccordionSummary-expandIconWrapper': { transition: 'transform 0.25s ease' },
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        m: 0,
                        fontSize: { xs: 15, md: 16.5 },
                        fontWeight: 600,
                        lineHeight: 1.45,
                        color: INK,
                        pr: 2,
                    }}
                >
                    <Trans i18nKey={`${namespace}.${idx}.q`} values={{ POK_NAME: resolvePoKName() }} />
                </Typography>
            </AccordionSummary>
            <AccordionDetails id={`faq_panel_${idx}`} sx={{ px: { xs: 2.5, md: 3.5 }, pt: 0, pb: 2.75 }}>
                <Typography
                    component="div"
                    sx={{
                        fontSize: 15,
                        fontWeight: 400,
                        lineHeight: 1.7,
                        color: '#4a4458',
                        '& a': { color: PURPLE, fontWeight: 600 },
                        '& ul': { mt: 1, mb: 0, pl: 2.5 },
                        '& li': { mb: 0.5 },
                    }}
                >
                    <Trans
                        i18nKey={`${namespace}.${idx}.a`}
                        values={{ POK_NAME: resolvePoKName() }}
                        components={{
                            ul: <ul />,
                            li: <li />,
                            h3: <h3 style={{ fontSize: '15px', margin: '8px 0 4px' }} />,
                            em: <em />,
                            comparisonPost: <a href={comparisonPostUrl}>default</a>,
                        }}
                    />
                </Typography>
            </AccordionDetails>
        </Accordion>
    );
}
